/**
 * @deprecated This Redux slice is deprecated. Use RTK Query instead:
 * - Import from: `redux/services/account/accountApi`
 * - Queries: `useGetCurrentUserQuery`
 * - Mutations: `useLoginMutation`, `useLogoutMutation`, `useUpdateAccountMutation`
 *
 * Note: Some auth state (currentUser) may need to remain in a slice for
 * synchronous access. Consider a hybrid approach during migration.
 *
 * This slice is maintained for backward compatibility only and will be removed in future versions.
 */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { deleteTokens, setToken } from "../../utils/authority";
import request, { renderQueries } from "../../utils/request";

const initialState = {
  currentUser: {
    active: false,
  },
  status: "idle",
  error: null,
};

export const fetchCurrentUser = createAsyncThunk(
  "account/fetchCurrentUser",
  async () => {
    const res = await request(`/account/current`);

    return res.data;
  }
);

export const userLogout = createAsyncThunk("account/userLogout", async () => {
  await request("/account/logout", { method: "post" });
  return { active: false, status: "success" };
});

export const forgotPassword = createAsyncThunk(
  "account/forgotPassword",
  async (data) => {
    const res = await request(`/account/forgot/password`, {
      method: "post",
      data,
    });
    return res.data;
  }
);

export const changePassword = createAsyncThunk(
  "account/changePassword",
  async ({ data, ...obj }) => {
    const queries = renderQueries(obj);
    const res = await request(`/account/change/password?${queries}`, {
      method: "post",
      data,
    });
    return res.data;
  }
);

export const userLogin = createAsyncThunk("account/userLogin", async (data) => {
  const res = await request(`/account/login`, {
    method: "post",
    data: { user: data },
  });

  // setToken(res.data.user.apiTok);
  return res.data;
});

export const updateAccount = createAsyncThunk(
  "account/updateAccount",
  async (data) => {
    const res = await request(`/account/update`, {
      method: "put",
      data: { user: data },
    });

    return res.data;
  }
);
export const activateAccount = createAsyncThunk(
  "account/activateAccount",
  async ({ data, ...obj }) => {
    const queries = renderQueries(obj);
    const res = await request(`/account/activate?${queries}`, {
      method: "put",
      data: { user: data },
    });

    return res.data;
  }
);

export const accountSlice = createSlice({
  name: "account",
  initialState,
  reducers: {
    updatedUser: (state, action) => {
      state.currentUser = { ...state.currentUser, ...action.payload };
    },
  },
  extraReducers(builder) {
    builder
      // FETCH CURRENT USER
      .addCase(fetchCurrentUser.pending, (state, action) => {
        state.status = "loading";
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // END FETCH CURRENT USER
      // FORGOT PASSWORD
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.status = "succeeded";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // END FORGOT PASSWORD
      // ADD NEW USER
      .addCase(userLogout.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      // END ADD NEW USER
      // LOGIN USER
      .addCase(userLogin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload.user;
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // END LOGIN USER
      // UPDATE USER
      .addCase(updateAccount.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload.user;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
    // END UPDATE USER
  },
});

export const { updatedUser } = accountSlice.actions;

export default accountSlice.reducer;

export const selectCurrentUser = (state) => state.account.currentUser;
export const selectAccountById = (state, accountId) =>
  state.account.account.find((account) => account.id === accountId);
